import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@material-ui/core'
import {
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  DateRange as DateRangeIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon
} from '@material-ui/icons'
import { Pagination } from '@material-ui/lab'
import { format, parseISO } from 'date-fns'
import { pt } from 'date-fns/locale'
import { useContext, useEffect, useReducer, useState } from 'react'

import DepartmentPermissionsModal from '../../components/DepartmentPermissionsModal'
import { InputFieldText } from '../../components/InputFieldText'
import ProtectedRoute from '../../components/ProtectedRoute'
import SelectInput from '../../components/SelectInput'
import TicketMessagesDialog from '../../components/TicketMessagesDialog'
import { AuthContext } from '../../context/Auth/AuthContext'
import { SocketContext } from '../../context/Socket/SocketContext'
import toastError from '../../errors/toastError'
import api from '../../services/api'

import { useStyles } from './style'

const reducer = (state, action) => {
  switch (action.type) {
    case 'LOAD_TICKETS':
      return [...action.payload]
    case 'UPDATE_TICKET': {
      const ticketIndex = state.findIndex(t => t.id === action.payload.id)
      if (ticketIndex !== -1) {
        state[ticketIndex] = action.payload
        return [...state]
      } else {
        return [action.payload, ...state]
      }
    }
    case 'DELETE_TICKET':
      return state.filter(t => t.id !== action.payload)
    case 'RESET':
      return []
    default:
      return state
  }
}

const PainelGestor = () => {
  const classes = useStyles()
  const { user } = useContext(AuthContext)
  const socketManager = useContext(SocketContext)

  // Estados principais - TODOS OS HOOKS DEVEM ESTAR NO TOPO
  const [tickets, dispatch] = useReducer(reducer, [])
  const [loading, setLoading] = useState(true)
  const [searchParam, setSearchParam] = useState('')
  const [viewMode, setViewMode] = useState('status') // 'status', 'department', 'user'
  const [showClosedTickets, setShowClosedTickets] = useState(true) // Controla exibição dos tickets fechados
  const [showMyTickets, setShowMyTickets] = useState(false) // Controla exibição da coluna "Meus Atendimentos"
  const [selectedDepartments, setSelectedDepartments] = useState([]) // Departamentos selecionados para filtro

  // Estados para filtros de data
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  // Estados para dados auxiliares
  const [departments, setDepartments] = useState([])
  const [users, setUsers] = useState([])
  const [closingReasons, setClosingReasons] = useState([])
  const [selectedClosingReasons, setSelectedClosingReasons] = useState([])

  // Estados para o popup de atendimento
  const [selectedTicketId, setSelectedTicketId] = useState(null)
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false)

  // Estados para o modal de permissões de departamentos
  const [departmentPermissionsModalOpen, setDepartmentPermissionsModalOpen] =
    useState(false)

  // Estados para paginação por coluna
  const [columnPages, setColumnPages] = useState({})
  const [ticketsPerPage] = useState(20)

  // const [isMainContentHidden, setIsMainContentHidden] = useState(false)

  // Buscar departamentos
  useEffect(() => {
    const fetchDepartmentsAndUserData = async () => {
      try {
        // Buscar dados atualizados do usuário atual da API para garantir que temos o visibleDepartmentIds mais recente
        let currentUserData = user
        if (user && user.id) {
          try {
            const { data: userData } = await api.get(`/users/${user.id}`)
            currentUserData = userData
          } catch (userError) {
            console.warn(
              'Erro ao buscar dados atualizados do usuário, usando dados do contexto:',
              userError
            )
            currentUserData = user
          }
        }

        const { data } = await api.get('/queue')
        // Filtrar departamentos baseado nas permissões do usuário
        let filteredDepartments = data

        // Se não é admin nem super, filtrar pelos departamentos vinculados ao usuário
        if (
          currentUserData &&
          !currentUserData.super &&
          currentUserData.profile !== 'admin'
        ) {
          // Usar user.queues para obter os departamentos vinculados ao usuário
          const userQueueIds =
            currentUserData.queues && Array.isArray(currentUserData.queues)
              ? currentUserData.queues.map(q => q.id)
              : []

          if (userQueueIds.length > 0) {
            filteredDepartments = data.filter(dept =>
              userQueueIds.includes(dept.id)
            )
          } else {
            // Fallback: tentar usar visibleDepartmentIds se queues não estiver disponível
            const visibleDepartmentIds =
              currentUserData.visibleDepartmentIds || []
            if (visibleDepartmentIds.length > 0) {
              filteredDepartments = data.filter(dept =>
                visibleDepartmentIds.includes(dept.id)
              )
            } else {
              // Se não tem departamentos vinculados, não mostra nenhum
              filteredDepartments = []
            }
          }
        }

        setDepartments(filteredDepartments)

        if (
          currentUserData?.visibleDepartmentIds &&
          Array.isArray(currentUserData.visibleDepartmentIds) &&
          currentUserData.visibleDepartmentIds.length > 0
        ) {
          const userVisibleDepartments =
            currentUserData.visibleDepartmentIds.filter(id => {
              const exists = data.some(dept => dept.id === id)
              return exists
            })

          const finalSelection = [...userVisibleDepartments, 'no-department']
          setSelectedDepartments(finalSelection)
        } else {
          setSelectedDepartments(['no-department'])
        }
      } catch (err) {
        console.error('❌ Erro ao buscar departamentos:', err)
        toastError(err)
      }
    }

    if (user) {
      fetchDepartmentsAndUserData()
    }
  }, [user])

  // Verificar mudanças no visibleDepartmentIds do usuário periodicamente
  useEffect(() => {
    if (!user || !user.id) return

    const checkUserChanges = async () => {
      try {
        const { data: userData } = await api.get(`/users/${user.id}`)

        // Verificar se o visibleDepartmentIds mudou
        const currentVisibleIds = selectedDepartments
          .filter(id => id !== 'no-department')
          .sort()
        const newVisibleIds = (userData.visibleDepartmentIds || []).sort()

        if (
          JSON.stringify(currentVisibleIds) !== JSON.stringify(newVisibleIds)
        ) {
          // Buscar departamentos para validar IDs
          const { data: departments } = await api.get('/queue')
          const validDepartments = newVisibleIds.filter(id =>
            departments.some(dept => dept.id === id)
          )

          setSelectedDepartments([...validDepartments, 'no-department'])
        }
      } catch (err) {
        console.error('Erro ao verificar mudanças do usuário:', err)
      }
    }

    // Verificar a cada 30 segundos
    const interval = setInterval(checkUserChanges, 30000)

    return () => clearInterval(interval)
  }, [user?.id, selectedDepartments])

  // Buscar usuários
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get('/users')
        setUsers(data.users || data)
      } catch (err) {
        toastError(err)
      }
    }
    fetchUsers()
  }, [])

  // Buscar motivos de finalização
  useEffect(() => {
    const fetchClosingReasons = async () => {
      try {
        const { data } = await api.get('/closing-reasons/list')
        setClosingReasons(data || [])
      } catch (err) {
        console.error('Erro ao buscar motivos de finalização:', err)
        // Não mostrar erro para o usuário se não conseguir buscar os motivos
      }
    }
    fetchClosingReasons()
  }, [])

  // Buscar tickets
  useEffect(() => {
    const fetchTickets = async () => {
      setLoading(true)
      try {
        const params = {
          searchParam: searchParam || undefined,
          showAll: 'true',
          all: 'true', // Remove limite de paginação para mostrar todos os tickets
          pageNumber: '1'
        }

        // Não enviar filtros de data para a API - faremos a filtragem no frontend
        // para permitir filtrar apenas tickets fechados por data de atualização

        // Remove undefined params
        Object.keys(params).forEach(key => {
          if (params[key] === undefined) {
            delete params[key]
          }
        })

        const { data } = await api.get('/tickets', { params })
        dispatch({ type: 'LOAD_TICKETS', payload: data.tickets })
      } catch (err) {
        toastError(err)
      } finally {
        setLoading(false)
      }
    }

    // Sempre executar a busca de tickets se o usuário existe
    if (user) {
      fetchTickets()
    }
  }, [searchParam, user])

  // Socket listeners - devem estar após a definição dos estados
  useEffect(() => {
    if (!user || !socketManager) return

    const socket = socketManager.GetSocket()

    const handleTicketUpdate = data => {
      if (data.action === 'update') {
        dispatch({ type: 'UPDATE_TICKET', payload: data.ticket })
      } else if (data.action === 'delete') {
        dispatch({ type: 'DELETE_TICKET', payload: data.ticketId })
      }
    }

    socket.on(`company-${user?.companyId}-ticket`, handleTicketUpdate)

    return () => {
      socket.off(`company-${user?.companyId}-ticket`, handleTicketUpdate)
    }
  }, [socketManager, user?.companyId])

  // Atualizar tickets a cada 30 segundos
  useEffect(() => {
    if (!user) return

    const interval = setInterval(() => {
      const fetchTickets = async () => {
        try {
          const params = {
            searchParam: searchParam || undefined,
            showAll: 'true',
            all: 'true',
            pageNumber: '1'
          }

          Object.keys(params).forEach(key => {
            if (params[key] === undefined) {
              delete params[key]
            }
          })

          const { data } = await api.get('/tickets', { params })
          dispatch({ type: 'LOAD_TICKETS', payload: data.tickets })
        } catch (err) {
          console.error('Erro ao atualizar tickets:', err)
        }
      }

      fetchTickets()
    }, 30000) // 30 segundos

    return () => clearInterval(interval)
  }, [searchParam, user])

  // Zerar filtros de data automaticamente quando ocultar tickets fechados
  useEffect(() => {
    if (!showClosedTickets) {
      // Sempre zerar os filtros quando ocultar fechados
      setDateFrom('')
      setDateTo('')
    }
  }, [showClosedTickets])

  // Resetar todas as páginas quando filtros mudarem
  useEffect(() => {
    resetAllPages()
  }, [
    searchParam,
    viewMode,
    selectedDepartments,
    selectedClosingReasons,
    showClosedTickets,
    showMyTickets,
    dateFrom,
    dateTo
  ])

  // Funções auxiliares
  // const getStatusColor = (status) => {
  //   switch (status) {
  //     case 'open':
  //       return 'primary';
  //     case 'pending':
  //       return 'secondary';
  //     case 'closed':
  //       return 'default';
  //     default:
  //       return 'default';
  //   }
  // };

  const getStatusLabel = status => {
    switch (status) {
      case 'open':
        return 'Aberto'
      case 'pending':
        return 'Pendente'
      case 'closed':
        return 'Fechado'
      default:
        return status
    }
  }

  // Função para calcular tempo decorrido
  const getTimeElapsed = (startDate, endDate = null) => {
    if (!startDate) return 'N/A'

    try {
      const start = parseISO(startDate)
      const end = endDate ? parseISO(endDate) : new Date()

      const diffInMinutes = Math.floor((end - start) / (1000 * 60))
      const diffInHours = Math.floor(diffInMinutes / 60)
      const diffInDays = Math.floor(diffInHours / 24)

      if (diffInDays > 0) {
        return `${diffInDays}d ${diffInHours % 24}h`
      } else if (diffInHours > 0) {
        return `${diffInHours}h ${diffInMinutes % 60}m`
      } else {
        return `${diffInMinutes}m`
      }
    } catch {
      return 'N/A'
    }
  }

  // Função para formatar data de forma segura
  const formatDate = dateString => {
    if (!dateString) return 'N/A'
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy HH:mm', { locale: pt })
    } catch {
      return dateString
    }
  }

  // Função para obter dados do contato de forma segura
  const getContactName = ticket => {
    return ticket?.contact?.name || 'Contato não informado'
  }

  // Função para obter dados da fila de forma segura
  const getQueueName = ticket => {
    return ticket?.queue?.name || '⚠️ Sem departamento'
  }

  // Função para obter dados do usuário de forma segura
  const getUserName = ticket => {
    return ticket?.user?.name || 'Não atribuído'
  }

  // Função para obter o nome do motivo de finalização
  const getClosingReasonName = ticket => {
    if (ticket.status !== 'closed') return null

    // Primeiro tenta usar o relacionamento direto da API
    if (ticket.closingReason && ticket.closingReason.name) {
      return ticket.closingReason.name
    }

    // Fallback: buscar na lista local
    if (ticket.closingReasonId) {
      const closingReason = closingReasons.find(
        reason => reason.id === ticket.closingReasonId
      )
      return closingReason?.name || 'Motivo não encontrado'
    }

    return 'Sem motivo informado'
  }

  const handleTicketClick = ticketId => {
    // Abrir popup em vez de navegar
    setSelectedTicketId(ticketId)
    setTicketDialogOpen(true)
  }

  const handleCloseTicketDialog = () => {
    setTicketDialogOpen(false)
    setSelectedTicketId(null)
  }

  // Função para limpar filtros de data
  const clearDateFilters = () => {
    setDateFrom('')
    setDateTo('')
  }

  // Função para gerenciar seleção de departamentos
  const handleDepartmentChange = async event => {
    const value = event.target.value
    const newSelectedDepartments =
      typeof value === 'string' ? value.split(',') : value
    setSelectedDepartments(newSelectedDepartments)

    // Salvar automaticamente as alterações no campo visibleDepartmentIds
    try {
      // Filtrar apenas os IDs numéricos dos departamentos (excluir "no-department")
      const departmentIds = newSelectedDepartments.filter(
        id => id !== 'no-department'
      )

      await api.put(`/users/${user.id}`, {
        visibleDepartmentIds: departmentIds
      })
    } catch (err) {
      console.error('Erro ao salvar departamentos para visualização:', err)
      toastError(err)
    }
  }

  // Função para selecionar/desselecionar todos os departamentos
  const handleSelectAllDepartments = async () => {
    const allOptions = [...departments.map(dept => dept.id), 'no-department']
    let newSelectedDepartments

    if (selectedDepartments.length === allOptions.length) {
      newSelectedDepartments = [] // Desselecionar todos
    } else {
      newSelectedDepartments = allOptions // Selecionar todos (incluindo sem departamento)
    }

    setSelectedDepartments(newSelectedDepartments)

    // Salvar automaticamente as alterações no campo visibleDepartmentIds
    try {
      // Filtrar apenas os IDs numéricos dos departamentos (excluir "no-department")
      const departmentIds = newSelectedDepartments.filter(
        id => id !== 'no-department'
      )

      await api.put(`/users/${user.id}`, {
        visibleDepartmentIds: departmentIds
      })
    } catch (err) {
      console.error(
        'Erro ao salvar departamentos para visualização (select all):',
        err
      )
      toastError(err)
    }
  }

  // Função específica para toggle all no MenuItem
  const toggleSelectAll = async e => {
    e.preventDefault()
    e.stopPropagation()
    await handleSelectAllDepartments()
  }

  // Função para gerenciar seleção de motivos de finalização
  const handleClosingReasonChange = event => {
    const value = event.target.value
    const newSelectedClosingReasons =
      typeof value === 'string' ? value.split(',') : value
    setSelectedClosingReasons(newSelectedClosingReasons)
  }

  // Função para selecionar/desselecionar todos os motivos de finalização
  const handleSelectAllClosingReasons = () => {
    const allClosingReasonOptions = [
      ...closingReasons.map(reason => reason.id),
      'no-reason'
    ]

    if (selectedClosingReasons.length === allClosingReasonOptions.length) {
      setSelectedClosingReasons([]) // Desselecionar todos
    } else {
      setSelectedClosingReasons(allClosingReasonOptions) // Selecionar todos
    }
  }

  // Função específica para toggle all no MenuItem dos motivos
  const toggleSelectAllClosingReasons = e => {
    e.preventDefault()
    e.stopPropagation()
    handleSelectAllClosingReasons()
  }

  // Função para filtrar tickets por motivo de finalização
  const filterTicketsByClosingReason = ticketList => {
    if (selectedClosingReasons.length === 0) {
      return ticketList // Se nenhum motivo selecionado, mostrar todos
    }

    return ticketList.filter(t => {
      // Se o ticket está fechado e tem motivo de finalização
      if (t.status === 'closed' && t.closingReasonId) {
        return selectedClosingReasons.includes(t.closingReasonId)
      }
      // Se o ticket não está fechado e incluímos "sem motivo"
      if (
        t.status !== 'closed' &&
        selectedClosingReasons.includes('no-reason')
      ) {
        return true
      }
      // Se o ticket está fechado mas não tem motivo e incluímos "sem motivo"
      if (
        t.status === 'closed' &&
        !t.closingReasonId &&
        selectedClosingReasons.includes('no-reason')
      ) {
        return true
      }
      return false
    })
  }

  // Funções para organizar tickets em colunas
  const getTicketsByStatus = () => {
    // Filtrar tickets por departamentos selecionados primeiro
    let departmentFilteredTickets = tickets.filter(t => {
      // Se tem departamento, verificar se está selecionado
      if (t.queueId) {
        return selectedDepartments.includes(t.queueId)
      }
      // Se não tem departamento, verificar se "sem departamento" está selecionado
      return selectedDepartments.includes('no-department')
    })

    // Aplicar filtro por motivo de finalização
    departmentFilteredTickets = filterTicketsByClosingReason(
      departmentFilteredTickets
    )

    const openTickets = departmentFilteredTickets.filter(
      t => t.status === 'open'
    )
    const pendingTickets = departmentFilteredTickets.filter(
      t => t.status === 'pending'
    )
    const closedTickets = departmentFilteredTickets.filter(
      t => t.status === 'closed'
    )

    const columns = []

    // Se fechados estão habilitados, adicionar como primeira coluna em vermelho
    if (showClosedTickets && closedTickets.length > 0) {
      columns.push({
        id: 'closed',
        title: `Fechados`,
        tickets: closedTickets,
        color: '#a31b1bff',
        totalTickets: closedTickets.length
      })
    }

    // Adicionar apenas colunas que têm tickets (com cores de fundo azuis)
    if (openTickets.length > 0) {
      columns.push({
        id: 'open',
        title: `Abertos`,
        tickets: openTickets,
        color: '#134264ff',
        totalTickets: openTickets.length
      })
    }

    if (pendingTickets.length > 0) {
      columns.push({
        id: 'pending',
        title: `Pendentes`,
        tickets: pendingTickets,
        color: '#134264ff',
        totalTickets: pendingTickets.length
      })
    }

    return columns
  }

  const getTicketsByDepartment = () => {
    const departmentGroups = {}

    // Filtrar apenas departamentos selecionados
    const filteredDepartments = departments.filter(dept =>
      selectedDepartments.includes(dept.id)
    )

    filteredDepartments.forEach(dept => {
      // Filtrar tickets fechados se a opção estiver desabilitada
      let deptTickets = tickets.filter(t => {
        const matchesDepartment = t.queueId === dept.id
        const shouldShow = showClosedTickets || t.status !== 'closed'
        return matchesDepartment && shouldShow
      })

      // Aplicar filtro por motivo de finalização
      deptTickets = filterTicketsByClosingReason(deptTickets)

      // Apenas adicionar departamento se tiver tickets
      if (deptTickets.length > 0) {
        departmentGroups[dept.id] = {
          id: `dept-${dept.id}`,
          title: `${dept.name}`,
          tickets: deptTickets,
          totalTickets: deptTickets.length
        }
      }
    })

    // Adicionar tickets sem departamento apenas se houver tickets
    let unassignedTickets = tickets.filter(t => {
      const hasNoDepartment = !t.queueId
      const shouldShow = showClosedTickets || t.status !== 'closed'
      return hasNoDepartment && shouldShow
    })

    // Aplicar filtro por motivo de finalização
    unassignedTickets = filterTicketsByClosingReason(unassignedTickets)

    if (unassignedTickets.length > 0) {
      departmentGroups['unassigned'] = {
        id: 'dept-unassigned',
        title: `⚠️ Sem Departamento`,
        tickets: unassignedTickets,
        color: '#6e1446ff', // Cor laranja claro para destaque
        totalTickets: unassignedTickets.length
      }
    }

    return Object.values(departmentGroups)
  }

  const getTicketsByUser = () => {
    const userGroups = {}

    users.forEach(userItem => {
      // Filtrar tickets fechados se a opção estiver desabilitada
      // E também filtrar por departamentos selecionados
      let userTickets = tickets.filter(t => {
        const matchesUser = t.userId === userItem.id
        const shouldShow = showClosedTickets || t.status !== 'closed'
        // Filtrar por departamentos: se tem departamento, deve estar selecionado; se não tem, "sem departamento" deve estar selecionado
        const departmentMatch = t.queueId
          ? selectedDepartments.includes(t.queueId)
          : selectedDepartments.includes('no-department')
        return matchesUser && shouldShow && departmentMatch
      })

      // Aplicar filtro por motivo de finalização
      userTickets = filterTicketsByClosingReason(userTickets)

      // Apenas adicionar usuário se tiver tickets
      if (userTickets.length > 0) {
        userGroups[userItem.id] = {
          id: `user-${userItem.id}`,
          title: `${userItem.name}`,
          tickets: userTickets,
          totalTickets: userTickets.length
        }
      }
    })

    // Adicionar tickets não atribuídos apenas se houver tickets
    // E também filtrar por departamentos selecionados
    let unassignedTickets = tickets.filter(t => {
      const hasNoUser = !t.userId
      const shouldShow = showClosedTickets || t.status !== 'closed'
      // Filtrar por departamentos: se tem departamento, deve estar selecionado; se não tem, "sem departamento" deve estar selecionado
      const departmentMatch = t.queueId
        ? selectedDepartments.includes(t.queueId)
        : selectedDepartments.includes('no-department')
      return hasNoUser && shouldShow && departmentMatch
    })

    // Aplicar filtro por motivo de finalização
    unassignedTickets = filterTicketsByClosingReason(unassignedTickets)

    if (unassignedTickets.length > 0) {
      userGroups['unassigned'] = {
        id: 'user-unassigned',
        title: `⚠️ Não Atribuído`,
        tickets: unassignedTickets,
        color: '#6e1446ff', // Cor laranja claro para destaque
        totalTickets: unassignedTickets.length
      }
    }

    return Object.values(userGroups)
  }

  // Função para obter tickets do usuário logado
  const getMyTickets = () => {
    if (!user || !user.id) return []

    let myTickets = tickets.filter(t => {
      const isMyTicket = t.userId === user.id
      const shouldShow = showClosedTickets || t.status !== 'closed'
      // Filtrar por departamentos: se tem departamento, deve estar selecionado; se não tem, "sem departamento" deve estar selecionado
      const departmentMatch = t.queueId
        ? selectedDepartments.includes(t.queueId)
        : selectedDepartments.includes('no-department')
      return isMyTicket && shouldShow && departmentMatch
    })

    // Aplicar filtro por motivo de finalização
    myTickets = filterTicketsByClosingReason(myTickets)

    return myTickets
  }

  const getColumns = () => {
    let columns = []

    // Obter colunas principais baseadas no modo de visualização
    switch (viewMode) {
      case 'status':
        columns = getTicketsByStatus()
        break
      case 'department':
        columns = getTicketsByDepartment()
        break
      case 'user':
        columns = getTicketsByUser()
        break
      default:
        columns = getTicketsByStatus()
    }

    // Adicionar coluna "Meus Atendimentos" se habilitada e se há tickets do usuário
    if (showMyTickets) {
      const myTickets = getMyTickets()
      if (myTickets.length > 0) {
        const myTicketsColumn = {
          id: 'my-tickets',
          title: `Meus Atendimentos`,
          tickets: myTickets,
          color: '#297c29ff', // Verde
          isMyTickets: true, // Flag para identificar a coluna
          totalTickets: myTickets.length
        }

        // Adicionar no início da lista
        columns.unshift(myTicketsColumn)
      }
    }

    // Aplicar paginação a cada coluna
    const paginatedColumns = columns.map(column => ({
      ...column,
      tickets: paginateTickets(column.tickets, column.id),
      totalTickets: column.totalTickets || column.tickets.length,
      currentPage: columnPages[column.id] || 1,
      totalPages: getTotalPagesForColumn(
        column.totalTickets || column.tickets.length
      )
    }))

    return paginatedColumns
  }

  // Estatísticas dos atendimentos
  const getStats = () => {
    // Aplicar filtro de departamentos em todos os cálculos
    let departmentFilteredTickets = tickets.filter(t => {
      // Filtrar por departamentos: se tem departamento, deve estar selecionado; se não tem, "sem departamento" deve estar selecionado
      return t.queueId
        ? selectedDepartments.includes(t.queueId)
        : selectedDepartments.includes('no-department')
    })

    // Aplicar filtro por motivo de finalização
    departmentFilteredTickets = filterTicketsByClosingReason(
      departmentFilteredTickets
    )

    const totalTickets = departmentFilteredTickets.length
    const openTickets = departmentFilteredTickets.filter(
      t => t.status === 'open'
    ).length
    const pendingTickets = departmentFilteredTickets.filter(
      t => t.status === 'pending'
    ).length
    const closedTickets = departmentFilteredTickets.filter(
      t => t.status === 'closed'
    ).length
    const withoutResponse = departmentFilteredTickets.filter(
      t => t.unreadMessages > 0
    ).length

    // Estatísticas dos meus atendimentos (também com filtro de departamento)
    const myTickets =
      user && user.id
        ? departmentFilteredTickets.filter(t => t.userId === user.id)
        : []
    const myOpenTickets = myTickets.filter(t => t.status === 'open').length
    const myPendingTickets = myTickets.filter(
      t => t.status === 'pending'
    ).length
    const myClosedTickets = myTickets.filter(t => t.status === 'closed').length

    // Tickets criados hoje (sempre considerando hoje, independente do filtro)
    const today = new Date()
    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    )

    // Se há filtro de data, contar apenas os do período filtrado que foram criados hoje
    // Se não há filtro, contar todos os criados hoje
    const ticketsToday = departmentFilteredTickets.filter(t => {
      if (!t.createdAt) return false
      try {
        const ticketDate = parseISO(t.createdAt)
        return ticketDate >= startOfToday
      } catch {
        return false
      }
    }).length

    // Tickets fechados hoje (considerando apenas os fechados hoje dentro do período filtrado)
    const closedToday = departmentFilteredTickets.filter(t => {
      if (t.status !== 'closed' || !t.updatedAt) return false
      try {
        const updatedDate = parseISO(t.updatedAt)
        return updatedDate >= startOfToday
      } catch {
        return false
      }
    }).length

    return {
      total: totalTickets,
      open: openTickets,
      pending: pendingTickets,
      closed: closedTickets,
      withoutResponse: withoutResponse,
      createdToday: ticketsToday,
      closedToday: closedToday,
      hasDateFilter: !!(dateFrom || dateTo), // Indica se há filtro ativo
      hasDepartmentFilter: selectedDepartments.length < departments.length + 1, // Indica se há filtro de departamento
      hasClosingReasonFilter: selectedClosingReasons.length > 0, // Indica se há filtro de motivo de finalização
      // Estatísticas dos meus atendimentos
      myTotal: myTickets.length,
      myOpen: myOpenTickets,
      myPending: myPendingTickets,
      myClosed: myClosedTickets
    }
  }

  const stats = getStats()

  const getStatusBorderColor = status => {
    switch (status) {
      case 'open':
        return '#4CAF50' // Verde
      case 'pending':
        return '#FF9800' // Laranja
      case 'closed':
        return '#b63737ff' // Cinza
      default:
        return '#2196F3' // Azul
    }
  }

  // Função para paginar uma lista de tickets com base em uma página específica da coluna
  const paginateTickets = (tickets, columnId) => {
    const currentPage = columnPages[columnId] || 1
    const startIndex = (currentPage - 1) * ticketsPerPage
    const endIndex = startIndex + ticketsPerPage
    return tickets.slice(startIndex, endIndex)
  }

  // Função para calcular o número total de páginas para uma coluna específica
  const getTotalPagesForColumn = totalTickets => {
    return Math.ceil(totalTickets / ticketsPerPage)
  }

  // Função para resetar todas as páginas quando filtros mudarem
  const resetAllPages = () => {
    setColumnPages({})
  }

  // Função para alterar a página de uma coluna específica
  const setColumnPage = (columnId, page) => {
    setColumnPages(prev => ({
      ...prev,
      [columnId]: page
    }))
  }

  return (
    <ProtectedRoute
      requiredRoles={['super', 'admin', 'gestor']}
      fallbackPath="/tickets"
    >
      {/* <MainContainer aria-hidden={isMainContentHidden}> */}
      <div className={classes.root}>
        <div className={classes.mainPaper}>
          {/* Header */}
          <Box className={classes.header}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              {/* Ícone de configuração no canto superior direito */}
              {/* {(user?.profile === 'admin' || user?.super) && (
                <Tooltip title="Configurar quais departamentos cada usuário pode visualizar no Painel de Gestor">
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<FilterListIcon />}
                    onClick={() => setDepartmentPermissionsModalOpen(true)}
                    size="small"
                    style={{
                      color: '#4CAF50',
                      borderColor: '#4CAF50',
                      '&:hover': {
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        borderColor: '#4CAF50'
                      }
                    }}
                  >
                    Configurar Permissões
                  </Button>
                </Tooltip>
              )} */}
            </Box>
          </Box>

          {/* Estatísticas */}
          <Box className={classes.statsContainer}>
            <Grid container spacing={2}>
              <Grid item xs={6} md={2}>
                <Card className={classes.statCard}>
                  <CardContent className={classes.statCardContent}>
                    <Typography variant="h4" className={classes.statNumberOpen}>
                      {stats.open}
                    </Typography>
                    <Typography variant="body2" className={classes.statLabel}>
                      Abertos
                    </Typography>
                    <Typography
                      variant="caption"
                      className={classes.statSecondary}
                    >
                      {stats.hasDateFilter ||
                      stats.hasDepartmentFilter ||
                      stats.hasClosingReasonFilter
                        ? 'Com filtros ativos'
                        : 'Total'}
                      : {stats.open} atendimentos
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={2}>
                <Card className={classes.statCard}>
                  <CardContent className={classes.statCardContent}>
                    <Typography
                      variant="h4"
                      className={classes.statNumberPending}
                    >
                      {stats.pending}
                    </Typography>
                    <Typography variant="body2" className={classes.statLabel}>
                      Pendentes
                    </Typography>
                    <Typography
                      variant="caption"
                      className={classes.statSecondary}
                    >
                      {stats.hasDateFilter ||
                      stats.hasDepartmentFilter ||
                      stats.hasClosingReasonFilter
                        ? 'Com filtros ativos'
                        : 'Total'}
                      : {stats.pending} atendimentos
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={2}>
                <Card className={classes.statCard}>
                  <CardContent className={classes.statCardContent}>
                    <Typography
                      variant="h4"
                      className={classes.statNumberClosed}
                    >
                      {stats.closed}
                    </Typography>
                    <Typography variant="body2" className={classes.statLabel}>
                      Fechados
                    </Typography>
                    <Typography
                      variant="caption"
                      className={classes.statSecondary}
                    >
                      {stats.hasDateFilter ||
                      stats.hasDepartmentFilter ||
                      stats.hasClosingReasonFilter
                        ? 'Com filtros ativos'
                        : 'Total'}
                      : {stats.closed} atendimentos
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={2}>
                <Card className={classes.statCard}>
                  <CardContent className={classes.statCardContent}>
                    <Typography
                      variant="h4"
                      className={classes.statNumberGeneral}
                    >
                      {stats.total}
                    </Typography>
                    <Typography variant="body2" className={classes.statLabel}>
                      {stats.hasDateFilter ||
                      stats.hasDepartmentFilter ||
                      stats.hasClosingReasonFilter
                        ? 'Total Filtrado'
                        : 'Total Geral'}
                    </Typography>
                    <Typography
                      variant="caption"
                      className={classes.statSecondary}
                    >
                      {stats.hasDateFilter ||
                      stats.hasDepartmentFilter ||
                      stats.hasClosingReasonFilter
                        ? 'Com filtros ativos'
                        : 'Total'}
                      : {stats.total} atendimentos
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={2}>
                <Card className={classes.statCard}>
                  <CardContent className={classes.statCardContent}>
                    <Typography
                      variant="h4"
                      className={classes.statNumberToday}
                    >
                      {stats.createdToday}
                    </Typography>
                    <Typography variant="body2" className={classes.statLabel}>
                      Criados Hoje
                    </Typography>
                    <Typography
                      variant="caption"
                      className={classes.statSecondary}
                    >
                      {stats.hasDateFilter ||
                      stats.hasDepartmentFilter ||
                      stats.hasClosingReasonFilter
                        ? `Hoje (filtrado): ${stats.createdToday}`
                        : `Novos hoje: ${stats.createdToday}`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={2}>
                <Card className={classes.statCard}>
                  <CardContent className={classes.statCardContent}>
                    <Typography
                      variant="h4"
                      className={classes.statNumberClosedToday}
                    >
                      {stats.closedToday}
                    </Typography>
                    <Typography variant="body2" className={classes.statLabel}>
                      Fechados Hoje
                    </Typography>
                    <Typography
                      variant="caption"
                      className={classes.statSecondary}
                    >
                      {stats.hasDateFilter ||
                      stats.hasDepartmentFilter ||
                      stats.hasClosingReasonFilter
                        ? `Hoje (filtrado): ${stats.closedToday}`
                        : `Resolvidos hoje: ${stats.closedToday}`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Estatísticas "Meus Atendimentos" - mostrar apenas quando habilitado */}
            {showMyTickets && stats.myTotal > 0 && (
              <Box style={{ marginTop: '16px' }}>
                <Typography
                  variant="h6"
                  style={{
                    marginBottom: '8px',
                    color: '#16c21eff',
                    fontWeight: 'bold'
                  }}
                >
                  📋 Meus Atendimentos ({user?.name || 'Usuário'})
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Card
                      className={classes.statCard}
                      style={{ border: '1px solid #16c21eff' }}
                    >
                      <CardContent className={classes.statCardContent}>
                        <Typography
                          variant="h3"
                          className={classes.statNumber}
                          style={{ color: '#16c21eff' }}
                        >
                          {stats.myTotal}
                        </Typography>
                        <Typography
                          variant="body2"
                          className={classes.statLabel}
                        >
                          Total Meus
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Atendimentos atribuídos a mim
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card
                      className={classes.statCard}
                      style={{ border: '1px solid #4CAF50' }}
                    >
                      <CardContent className={classes.statCardContent}>
                        <Typography
                          variant="h3"
                          className={classes.statNumber}
                          style={{ color: '#4CAF50' }}
                        >
                          {stats.myOpen}
                        </Typography>
                        <Typography
                          variant="body2"
                          className={classes.statLabel}
                        >
                          Meus Abertos
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Atendimentos abertos atribuídos
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card
                      className={classes.statCard}
                      style={{ border: '1px solid #FF9800' }}
                    >
                      <CardContent className={classes.statCardContent}>
                        <Typography
                          variant="h3"
                          className={classes.statNumber}
                          style={{ color: '#FF9800' }}
                        >
                          {stats.myPending}
                        </Typography>
                        <Typography
                          variant="body2"
                          className={classes.statLabel}
                        >
                          Meus Pendentes
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Atendimentos pendentes atribuídos
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card
                      className={classes.statCard}
                      style={{ border: '1px solid #a31b1bff' }}
                    >
                      <CardContent className={classes.statCardContent}>
                        <Typography
                          variant="h3"
                          className={classes.statNumber}
                          style={{ color: '#a31b1bff' }}
                        >
                          {stats.myClosed}
                        </Typography>
                        <Typography
                          variant="body2"
                          className={classes.statLabel}
                        >
                          Meus Fechados
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Atendimentos fechados atribuídos
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>

          {/* Controles de Visualização */}
          <Box className={classes.controlsContainer}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} md={2}>
                <InputFieldText
                  fullWidth
                  placeholder="Buscar atendimentos"
                  value={searchParam}
                  onChange={e => setSearchParam(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        className={classes.iconSearch}
                      >
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              {/* Filtros de Departamento */}
              <Grid item xs={12} md={2}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <SelectInput
                    multiple
                    value={selectedDepartments}
                    onChange={handleDepartmentChange}
                    label="Departamentos"
                    renderValue={selected =>
                      `${selected.length} de ${
                        departments.length + 1
                      } selecionados`
                    }
                  >
                    <MenuItem
                      onClick={toggleSelectAll}
                      style={{
                        fontWeight: 'bold',
                        color: '#2196F3',
                        cursor: 'pointer'
                      }}
                    >
                      <Checkbox
                        checked={
                          selectedDepartments.length === departments.length + 1
                        }
                        indeterminate={
                          selectedDepartments.length > 0 &&
                          selectedDepartments.length < departments.length + 1
                        }
                        onClick={toggleSelectAll}
                      />
                      <ListItemText
                        primary={
                          selectedDepartments.length === departments.length + 1
                            ? 'Desmarcar Todos'
                            : 'Selecionar Todos'
                        }
                      />
                    </MenuItem>
                    {departments.map(department => (
                      <MenuItem key={department.id} value={department.id}>
                        <Checkbox
                          checked={
                            selectedDepartments.indexOf(department.id) > -1
                          }
                        />
                        <ListItemText primary={department.name} />
                      </MenuItem>
                    ))}
                    <MenuItem key="no-department" value="no-department">
                      <Checkbox
                        checked={
                          selectedDepartments.indexOf('no-department') > -1
                        }
                      />
                      <ListItemText primary="⚠️ Sem Departamento" />
                    </MenuItem>
                  </SelectInput>
                </FormControl>
              </Grid>
              {/* Filtros de Motivo de Finalização */}
              <Grid item xs={12} md={2}>
                <FormControl variant="outlined" size="small" fullWidth>
                  {/* <InputLabel>Filtrar por Motivo de Finalização</InputLabel> */}
                  <SelectInput
                    multiple
                    value={selectedClosingReasons}
                    onChange={handleClosingReasonChange}
                    label="Motivo de Finalização"
                    renderValue={selected => {
                      if (selected.length === 0) return 'Todos os motivos'
                      return `${selected.length} motivo(s) selecionado(s)`
                    }}
                    // startAdornment={
                    //   <InputAdornment position="start">
                    //     <FilterListIcon />
                    //   </InputAdornment>
                    // }
                  >
                    <MenuItem
                      onClick={toggleSelectAllClosingReasons}
                      style={{
                        fontWeight: 'bold',
                        color: '#2196F3',
                        cursor: 'pointer'
                      }}
                    >
                      <Checkbox
                        checked={
                          selectedClosingReasons.length ===
                          closingReasons.length + 1
                        }
                        indeterminate={
                          selectedClosingReasons.length > 0 &&
                          selectedClosingReasons.length <
                            closingReasons.length + 1
                        }
                        onClick={toggleSelectAllClosingReasons}
                      />
                      <ListItemText
                        primary={
                          selectedClosingReasons.length ===
                          closingReasons.length + 1
                            ? 'Desmarcar Todos'
                            : 'Selecionar Todos'
                        }
                      />
                    </MenuItem>
                    {closingReasons.map(reason => (
                      <MenuItem key={reason.id} value={reason.id}>
                        <Checkbox
                          checked={
                            selectedClosingReasons.indexOf(reason.id) > -1
                          }
                        />
                        <ListItemText primary={reason.name} />
                      </MenuItem>
                    ))}
                    <MenuItem key="no-reason" value="no-reason">
                      <Checkbox
                        checked={
                          selectedClosingReasons.indexOf('no-reason') > -1
                        }
                      />
                      <ListItemText primary="⚠️ Sem motivo / Não fechados" />
                    </MenuItem>
                  </SelectInput>
                </FormControl>
              </Grid>
              {/* Radio Groups */}
              <Grid item xs={12} md={4}>
                <Box display="flex" gap={1}>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      aria-label="view-mode"
                      name="view-mode"
                      value={viewMode}
                      onChange={e => setViewMode(e.target.value)}
                    >
                      <FormControlLabel
                        value="status"
                        control={<Radio size="small" color="primary" />}
                        label="Por Status"
                        labelPlacement="bottom"
                      />
                      <FormControlLabel
                        value="department"
                        control={<Radio size="small" color="primary" />}
                        label="Por Departamento"
                        labelPlacement="bottom"
                      />
                      <FormControlLabel
                        value="user"
                        control={<Radio size="small" color="primary" />}
                        label="Por Usuário"
                        labelPlacement="bottom"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box display="flex" justifyContent="flex-end" gap={1}>
                  <Button
                    variant={showMyTickets ? 'contained' : 'outlined'}
                    color={showMyTickets ? 'primary' : 'default'}
                    onClick={() => setShowMyTickets(!showMyTickets)}
                    size="small"
                    startIcon={<PersonIcon />}
                  >
                    {showMyTickets ? 'Ocultar Meus' : 'Meus Atendimentos'}
                  </Button>
                  <Button
                    variant={showClosedTickets ? 'contained' : 'outlined'}
                    color={showClosedTickets ? 'default' : 'secondary'}
                    onClick={() => setShowClosedTickets(!showClosedTickets)}
                    size="small"
                    startIcon={
                      showClosedTickets ? (
                        <VisibilityIcon />
                      ) : (
                        <CheckCircleIcon />
                      )
                    }
                  >
                    {showClosedTickets ? 'Ocultar Fechados' : 'Exibir Fechados'}
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {/* Linha adicional para filtro de motivo de finalização */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box display="flex" justifyContent="flex-end" gap={1}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setSelectedClosingReasons([])}
                    size="small"
                    disabled={selectedClosingReasons.length === 0}
                  >
                    Limpar Filtro de Motivos
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Filtros de Data - mostrar apenas quando tickets fechados estão visíveis */}
          {showClosedTickets && (
            <Box
              className={classes.controlsContainer}
              style={{ marginTop: '16px' }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Data Inicial Fechamento"
                    type="date"
                    value={dateFrom}
                    onChange={e => setDateFrom(e.target.value)}
                    InputLabelProps={{
                      shrink: true
                    }}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Data Final Fechamento"
                    type="date"
                    value={dateTo}
                    onChange={e => setDateTo(e.target.value)}
                    InputLabelProps={{
                      shrink: true
                    }}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={clearDateFilters}
                    size="small"
                    disabled={!dateFrom && !dateTo}
                  >
                    Limpar Filtro de Data
                  </Button>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    {dateFrom && dateTo
                      ? `Fechamentos: ${format(
                          new Date(dateFrom + 'T00:00:00'),
                          'dd/MM/yyyy'
                        )} - ${format(
                          new Date(dateTo + 'T00:00:00'),
                          'dd/MM/yyyy'
                        )}`
                      : dateFrom
                        ? `Fechados a partir de: ${format(
                            new Date(dateFrom + 'T00:00:00'),
                            'dd/MM/yyyy'
                          )}`
                        : dateTo
                          ? `Fechados até: ${format(
                              new Date(dateTo + 'T00:00:00'),
                              'dd/MM/yyyy'
                            )}`
                          : 'Sem filtro de data - Exibindo todos os tickets'}
                  </Typography>
                  {(dateFrom || dateTo) && (
                    <Typography
                      variant="caption"
                      style={{
                        color: '#2196F3',
                        fontWeight: 'bold',
                        display: 'block'
                      }}
                    >
                      ⚡ Filtro de data ativo - Apenas tickets fechados no
                      período selecionado
                    </Typography>
                  )}
                  {stats.hasDepartmentFilter && (
                    <Typography
                      variant="caption"
                      style={{
                        color: '#FF9800',
                        fontWeight: 'bold',
                        display: 'block'
                      }}
                    >
                      🏢 Filtro de departamento ativo -{' '}
                      {selectedDepartments.length} de {departments.length + 1}{' '}
                      selecionados
                    </Typography>
                  )}
                  {stats.hasClosingReasonFilter && (
                    <Typography
                      variant="caption"
                      style={{
                        color: '#9C27B0',
                        fontWeight: 'bold',
                        display: 'block'
                      }}
                    >
                      🎯 Filtro de motivo ativo -{' '}
                      {selectedClosingReasons.length} motivo(s) selecionado(s)
                    </Typography>
                  )}
                  {(dateFrom || dateTo) && (
                    <Typography
                      variant="caption"
                      style={{
                        color: '#a31b1bff',
                        fontStyle: 'italic',
                        display: 'block',
                        marginTop: '4px'
                      }}
                    >
                      💡 O filtro de data se aplica apenas aos tickets fechados
                      (por data de fechamento)
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Título da Segunda Seção */}
          <Box className={classes.sectionTitle}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h5" className={classes.title}>
                {showClosedTickets
                  ? 'Todos os Atendimentos'
                  : 'Atendimentos Ativos'}
              </Typography>
              {(selectedDepartments.length < departments.length + 1 ||
                selectedClosingReasons.length > 0) && (
                <Box>
                  {selectedDepartments.length < departments.length + 1 && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ fontStyle: 'italic' }}
                    >
                      📊 Filtrando {selectedDepartments.length} de{' '}
                      {departments.length + 1} departamentos
                    </Typography>
                  )}
                  {selectedClosingReasons.length > 0 && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ fontStyle: 'italic' }}
                    >
                      🎯 Filtrando por {selectedClosingReasons.length} motivo(s)
                      de finalização
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* Colunas de Tickets */}
          {loading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress />
              <Typography variant="body1" style={{ marginTop: 16 }}>
                Carregando atendimentos...
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2} className={classes.columnsContainer}>
              {getColumns().length === 0 ? (
                <Grid item xs={12}>
                  <Card className={classes.columnCard}>
                    <Box
                      className={classes.emptyColumn}
                      style={{ padding: '40px', textAlign: 'center' }}
                    >
                      <Typography variant="h6" color="textSecondary">
                        Nenhum atendimento encontrado
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ marginTop: '8px' }}
                      >
                        {!showClosedTickets
                          ? 'Todos os atendimentos estão fechados. Clique em "Exibir Fechados" para visualizá-los.'
                          : 'Não há atendimentos para os filtros aplicados.'}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ) : (
                getColumns().map((column, index) => {
                  const columns = getColumns()
                  let columnWidth = 4 // Padrão para 3 colunas

                  if (columns.length === 1) {
                    columnWidth = 12 // Uma coluna ocupa toda a largura
                  } else if (columns.length === 2) {
                    columnWidth = 6 // Duas colunas, cada uma ocupa metade
                  } else if (columns.length === 3) {
                    columnWidth = 4 // Três colunas, cada uma ocupa um terço
                  } else if (columns.length === 4) {
                    columnWidth = 3 // Quatro colunas, cada uma ocupa um quarto
                  } else {
                    columnWidth = 3 // Mais de 4 colunas, cada uma ocupa um quarto (com scroll horizontal)
                  }

                  return (
                    <Grid item xs={12} md={columnWidth} key={index}>
                      <Card
                        className={classes.columnCard}
                        style={{
                          border: column.isMyTickets
                            ? '2px solid #297c29ff'
                            : 'none',
                          boxShadow: column.isMyTickets
                            ? '0 4px 8px rgba(233, 30, 99, 0.2)'
                            : undefined,
                          backgroundColor: column.color || 'transparent'
                        }}
                      >
                        <Box className={classes.columnHeader}>
                          <Typography
                            variant="h6"
                            className={classes.columnTitle}
                            style={{
                              color: column.isMyTickets
                                ? '#ffffffff'
                                : 'inherit',
                              fontWeight: column.isMyTickets ? 'bold' : 'normal'
                            }}
                          >
                            {column.title}
                          </Typography>
                          <Box className={classes.columnCount}>
                            <Typography variant="body2">
                              {column.totalTickets || column.tickets.length}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Informações de paginação */}
                        {column.totalTickets > ticketsPerPage && (
                          <Box
                            style={{
                              padding: '0 16px 8px',
                              color: column.isMyTickets
                                ? '#ffffffff'
                                : 'inherit'
                            }}
                          >
                            <Typography
                              variant="caption"
                              style={{ fontSize: '0.75rem' }}
                            >
                              Página {column.currentPage} de {column.totalPages}{' '}
                              • Mostrando {column.tickets.length} de{' '}
                              {column.totalTickets} atendimentos
                            </Typography>
                          </Box>
                        )}

                        <Box className={classes.ticketsColumn}>
                          {column.tickets.length === 0 ? (
                            <Box className={classes.emptyColumn}>
                              <Typography variant="body2" color="textSecondary">
                                Nenhum atendimento
                              </Typography>
                            </Box>
                          ) : (
                            column.tickets.map(ticket => (
                              <Card
                                key={ticket.id}
                                className={classes.ticketMiniCard}
                              >
                                <CardActionArea
                                  onClick={() => handleTicketClick(ticket.id)}
                                  className={classes.ticketMiniCardAction}
                                >
                                  <CardContent
                                    className={classes.ticketMiniCardContent}
                                  >
                                    <Box className={classes.ticketMiniHeader}>
                                      <Avatar className={classes.contactAvatar}>
                                        {getContactName(ticket).charAt(0)}
                                      </Avatar>
                                      <Box className={classes.ticketMiniInfo}>
                                        <Typography
                                          variant="subtitle2"
                                          className={classes.contactName}
                                        >
                                          {getContactName(ticket)}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          className={classes.contactNumber}
                                        >
                                          {ticket.contact?.number ||
                                            '(67) 99999-0000'}
                                        </Typography>
                                      </Box>
                                      <Box className={classes.ticketMiniStatus}>
                                        <Chip
                                          size="small"
                                          label={getStatusLabel(ticket.status)}
                                          style={{
                                            backgroundColor:
                                              getStatusBorderColor(
                                                ticket.status
                                              ),
                                            color: 'white',
                                            fontSize: '0.75rem'
                                          }}
                                        />
                                      </Box>
                                    </Box>

                                    <Box className={classes.ticketMiniDetails}>
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                      >
                                        Última atualização:{' '}
                                        {formatDate(ticket.updatedAt)}
                                      </Typography>
                                      {ticket.unreadMessages > 0 && (
                                        <Badge
                                          badgeContent={ticket.unreadMessages}
                                          color="error"
                                          className={classes.unreadBadge}
                                        />
                                      )}
                                    </Box>

                                    {/* Datas de Abertura e Fechamento */}
                                    <Box
                                      className={classes.ticketMiniDetails}
                                      style={{ marginTop: '8px' }}
                                    >
                                      <Box
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          marginBottom: '4px'
                                        }}
                                      >
                                        <DateRangeIcon
                                          style={{
                                            fontSize: '12px',
                                            marginRight: '4px',
                                            color: '#4CAF50'
                                          }}
                                        />
                                        <Typography
                                          variant="caption"
                                          color="textSecondary"
                                        >
                                          Abertura:{' '}
                                          {formatDate(ticket.createdAt)}
                                        </Typography>
                                      </Box>
                                      <Box
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          marginBottom: '4px'
                                        }}
                                      >
                                        <LockIcon
                                          style={{
                                            fontSize: '12px',
                                            marginRight: '4px',
                                            color:
                                              ticket.status === 'closed'
                                                ? '#a31b1bff'
                                                : '#6e1446ff'
                                          }}
                                        />
                                        <Typography
                                          variant="caption"
                                          color="textSecondary"
                                        >
                                          Fechamento:{' '}
                                          {ticket.status === 'closed'
                                            ? formatDate(ticket.updatedAt)
                                            : '-'}
                                        </Typography>
                                      </Box>
                                      <Box
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          marginBottom: '4px'
                                        }}
                                      >
                                        <AccessTimeIcon
                                          style={{
                                            fontSize: '12px',
                                            marginRight: '4px',
                                            color: '#2196F3'
                                          }}
                                        />
                                        <Typography
                                          variant="caption"
                                          color="textSecondary"
                                        >
                                          Tempo:{' '}
                                          {getTimeElapsed(
                                            ticket.createdAt,
                                            ticket.status === 'closed'
                                              ? ticket.updatedAt
                                              : null
                                          )}
                                        </Typography>
                                      </Box>
                                      {ticket.status === 'closed' && (
                                        <Box
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '4px',
                                            backgroundColor:
                                              'rgba(156, 39, 176, 0.1)',
                                            borderRadius: '4px',
                                            marginTop: '4px'
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            style={{
                                              color: '#9C27B0',
                                              fontWeight: 'bold',
                                              fontSize: '0.75rem'
                                            }}
                                          >
                                            🎯 Motivo:{' '}
                                            {getClosingReasonName(ticket)}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>

                                    <Box className={classes.ticketMiniFooter}>
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                      >
                                        {getUserName(ticket)}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                      >
                                        {getQueueName(ticket)}
                                      </Typography>
                                    </Box>
                                  </CardContent>
                                </CardActionArea>
                              </Card>
                            ))
                          )}
                        </Box>

                        {/* Paginação individual da coluna */}
                        {column.totalPages > 1 && (
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            style={{
                              padding: '16px',
                              borderTop: '1px solid rgba(0,0,0,0.1)',
                              backgroundColor: column.color
                                ? 'rgba(255,255,255,0.1)'
                                : '#f5f5f5'
                            }}
                          >
                            <Pagination
                              count={column.totalPages}
                              page={column.currentPage}
                              onChange={(event, page) =>
                                setColumnPage(column.id, page)
                              }
                              color="primary"
                              size="small"
                              showFirstButton
                              showLastButton
                              style={{
                                '& .MuiPaginationItem-root': {
                                  color: column.color ? 'white' : 'inherit'
                                }
                              }}
                            />
                          </Box>
                        )}
                      </Card>
                    </Grid>
                  )
                })
              )}
            </Grid>
          )}
        </div>

        {/* Popup de Atendimento */}
        {selectedTicketId && (
          <TicketMessagesDialog
            open={ticketDialogOpen}
            handleClose={handleCloseTicketDialog}
            ticketId={selectedTicketId}
          />
        )}

        {/* Modal de Permissões de Departamentos */}
        <DepartmentPermissionsModal
          open={departmentPermissionsModalOpen}
          onClose={() => setDepartmentPermissionsModalOpen(false)}
          onUpdate={() => {
            // Recarregar dados quando permissões forem atualizadas
            window.location.reload()
          }}
        />
      </div>
      {/* </MainContainer> */}
    </ProtectedRoute>
  )
}

export default PainelGestor
